/**
 * UI Size Classes Utility
 * Centralized size classes for UI components to make testing and adjusting sizes easier
 * Modify these values to test different size configurations across all components
 */

/**
 * Base size properties for composition
 * These can be mixed and matched to create custom sizes
 */
export const sizeProperties = {
	heights: {
		xs: 'h-6',
		sm: 'h-7',
		md: 'h-8',
		lg: 'h-9',
		xl: 'h-10',
	},
	paddingX: {
		xs: 'px-1.5',
		sm: 'px-2',
		md: 'px-2.5',
		lg: 'px-4',
		xl: 'px-5',
	},
	paddingY: {
		xs: 'py-0.5',
		sm: 'py-1',
		md: 'py-1.5',
		lg: 'py-2',
		xl: 'py-2.5',
	},
	gaps: {
		xs: 'gap-1',
		sm: 'gap-1.5',
		md: 'gap-2',
		lg: 'gap-2.5',
		xl: 'gap-3',
	},
	// Textarea-specific min-heights (not for general use)
	textareaMinHeights: {
		xs: 'min-h-5',
		sm: 'min-h-14',
		md: 'min-h-16',
		lg: 'min-h-24',
		xsAuto: 'min-h-6',
		smAuto: 'min-h-7',
		mdAuto: 'min-h-8',
		lgAuto: 'min-h-9',
	},
	textSizes: {
		xxs: 'text-[10px]',
		xs: 'text-xs',
		sm: 'text-sm',
		md: 'text-base',
		lg: 'text-lg',
	},
};

/**
 * Get a specific size property value
 * @param {string} property - Property name ('heights', 'paddingX', 'paddingY', 'gaps', 'textareaMinHeights', 'textSizes')
 * @param {string} size - Size key (e.g., 'xs', 'sm', 'md', 'lg', 'xl')
 * @returns {string} Size class or empty string if not found
 */
export const getSizeProperty = (property, size) => {
	const props = sizeProperties[property];
	if (!props) {
		console.warn(`Unknown property: ${property}`);
		return '';
	}
	return props[size] || '';
};

/**
 * Compose size classes from individual properties
 *
 * Usage examples:
 * - composeSize({ height: 'md', paddingX: 'md', gap: 'md' }) // Standard button size
 * - composeSize({ height: 'sm', paddingX: 'lg' }) // Large padding, small height
 * - composeSize({ height: 'md', extra: ['rounded-lg', 'font-bold'] }) // With extra classes
 * - composeSize({ paddingX: 'md', paddingY: 'md', extra: ['min-h-16'] }) // Textarea size (use extra for textarea-specific min-heights)
 *
 * @param {Object} options - Size composition options
 * @param {string} [options.height] - Height size key (xs, sm, md, lg, xl)
 * @param {string} [options.paddingX] - Horizontal padding size key
 * @param {string} [options.paddingY] - Vertical padding size key
 * @param {string} [options.gap] - Gap size key
 * @param {string} [options.textSize] - Text size key
 * @param {string[]} [options.extra] - Extra classes to include (use this for textarea min-heights from textareaMinHeights)
 * @param {Object} [options.custom] - Custom property map for advanced composition
 * @returns {string} Composed size classes
 */
export const composeSize = ({
	height,
	paddingX,
	paddingY,
	gap,
	textSize,
	extra = [],
	custom = {},
} = {}) => {
	const classes = [];

	// Standard properties
	if (height && sizeProperties.heights[height]) {
		classes.push(sizeProperties.heights[height]);
	}
	if (paddingX && sizeProperties.paddingX[paddingX]) {
		classes.push(sizeProperties.paddingX[paddingX]);
	}
	if (paddingY && sizeProperties.paddingY[paddingY]) {
		classes.push(sizeProperties.paddingY[paddingY]);
	}
	if (gap && sizeProperties.gaps[gap]) {
		classes.push(sizeProperties.gaps[gap]);
	}
	if (textSize && sizeProperties.textSizes[textSize]) {
		classes.push(sizeProperties.textSizes[textSize]);
	}

	// Custom properties (for direct access to sizeProperties)
	Object.entries(custom).forEach(([property, size]) => {
		const value = getSizeProperty(property, size);
		if (value) {
			classes.push(value);
		}
	});

	// Extra classes
	if (Array.isArray(extra)) {
		classes.push(...extra.filter(Boolean));
	} else if (typeof extra === 'string') {
		classes.push(extra);
	}

	return classes.join(' ');
};

/**
 * Create a custom size variant by mixing properties
 *
 * Usage examples:
 * - createCustomSize('button', { height: 'lg', paddingX: 'sm' }) // Large height, small padding
 * - createCustomSize('input', { height: 'xl' }) // Extra large input
 *
 * @param {string} baseType - Base component type ('button', 'input', 'textarea', 'select', 'badge')
 * @param {Object} overrides - Properties to override from the base size
 * @returns {string} Custom size classes
 */
export const createCustomSize = (baseType, overrides = {}) => {
	const baseSizes = {
		button: buttonSizes,
		input: inputSizes,
		textarea: textareaSizes,
		select: selectSizes,
		badge: badgeSizes,
	};

	const base = baseSizes[baseType];
	if (!base) {
		console.warn(`Unknown base type: ${baseType}`);
		return '';
	}

	// Get a default size as base (prefer 'md' or 'default')
	const baseSize = base.md || base.default || Object.values(base)[0];

	// Merge with overrides using composeSize
	return composeSize({
		...overrides,
		extra: baseSize ? [baseSize] : [],
	});
};

/**
 * Button size classes
 * Format: { size: 'padding-x gap height' }
 * Can be overridden by using composeSize() for custom combinations
 */
export const buttonSizes = {
	xs: composeSize({height: 'xs', paddingX: 'xs', gap: 'xs'}),
	sm: composeSize({height: 'sm', paddingX: 'sm', gap: 'sm'}),
	default: composeSize({height: 'md', paddingX: 'md', gap: 'md'}),
	md: composeSize({height: 'md', paddingX: 'md', gap: 'md'}),
	lg: composeSize({height: 'lg', paddingX: 'lg', gap: 'lg'}),
};

/**
 * Input size classes
 * Format: { size: 'height' }
 * Can be overridden by using composeSize() for custom combinations
 */
export const inputSizes = {
	xs: composeSize({height: 'xs', textSize: 'xs'}),
	sm: composeSize({height: 'sm', textSize: 'xs'}),
	md: composeSize({height: 'md', textSize: 'sm'}),
	lg: composeSize({height: 'lg', textSize: 'md'}),
};

/**
 * Textarea size classes
 * Format: { size: 'min-height, padding, and other classes' }
 * Can be overridden by using composeSize() for custom combinations
 */
export const textareaSizes = {
	xs: composeSize({
		paddingX: 'xs',
		paddingY: 'xs',
		extra: [sizeProperties.textareaMinHeights.xs],
	}),
	'xs-auto': composeSize({
		paddingX: 'xs',
		paddingY: 'xs',
		extra: [sizeProperties.textareaMinHeights.xsAuto, 'resize-none', 'overflow-hidden'],
	}),
	sm: composeSize({
		paddingX: 'sm',
		paddingY: 'sm',
		extra: [sizeProperties.textareaMinHeights.sm],
	}),
	'sm-auto': composeSize({
		paddingX: 'sm',
		paddingY: 'sm',
		extra: [sizeProperties.textareaMinHeights.smAuto, 'resize-none', 'overflow-hidden'],
	}),
	md: composeSize({
		paddingX: 'md',
		paddingY: 'md',
		extra: [sizeProperties.textareaMinHeights.md],
	}),
	'md-auto': composeSize({
		paddingX: 'md',
		paddingY: 'md',
		extra: [sizeProperties.textareaMinHeights.mdAuto, 'resize-none', 'overflow-hidden'],
	}),
	lg: composeSize({
		paddingX: 'md',
		paddingY: 'lg',
		extra: [sizeProperties.textareaMinHeights.lg],
	}),
	'lg-auto': composeSize({
		paddingX: 'md',
		paddingY: 'lg',
		extra: [sizeProperties.textareaMinHeights.lgAuto, 'resize-none', 'overflow-hidden'],
	}),
};

/**
 * Select size classes
 * Format: { size: 'height and padding' }
 * Can be overridden by using composeSize() for custom combinations
 */
export const selectSizes = {
	xs: composeSize({height: 'xs'}),
	sm: composeSize({height: 'sm'}),
	md: composeSize({height: 'md'}),
	lg: composeSize({height: 'lg'}),
};

/**
 * Badge size classes
 * Can be overridden by using composeSize() for custom combinations
 */
export const badgeSizes = {
	xs: composeSize({paddingX: 'xs', paddingY: 'xs', textSize: 'xs'}),
	sm: composeSize({paddingX: 'sm', paddingY: 'xs', textSize: 'xs'}),
	md: composeSize({paddingX: 'sm', paddingY: 'xs', textSize: 'sm'}),
	lg: composeSize({paddingX: 'md', paddingY: 'sm', textSize: 'sm'}),
};

/**
 * Menu row size classes (for list-like items: MenuItem, NavMenuItem, DropdownMenuItem)
 * Centralizes padding and text sizing for menu rows
 */
export const menuRowSizes = {
	xs: composeSize({paddingX: 'xs', paddingY: 'xs', textSize: 'xs'}),
	sm: composeSize({paddingX: 'sm', paddingY: 'xs', textSize: 'xs'}),
	md: composeSize({paddingX: 'md', paddingY: 'sm', textSize: 'sm'}),
	lg: composeSize({paddingX: 'lg', paddingY: 'md', textSize: 'md'}),
};

/**
 * Well padding sizes
 * Separate mappings for outer container padding/spacing and inner content padding
 */
export const wellSizes = {
	xs: composeSize({extra: ['p-0', 'space-y-0']}),
	sm: composeSize({extra: ['p-0.5', 'space-y-0.5']}),
	md: composeSize({extra: ['p-1', 'space-y-1']}),
	lg: composeSize({extra: ['p-1.5', 'space-y-1']}),
};

/**
 * BuildingTypeaheadSelect list item size classes
 * Format: { size: 'height' }
 * Custom heights for building typeahead select list items
 * Can be overridden by using composeSize() for custom combinations
 */
export const buildingTypeaheadSelectListItemSizes = {
	sm: composeSize({extra: ['h-[26px]']}),
	md: composeSize({extra: ['h-[30px]']}),
	lg: composeSize({extra: ['h-[38px]']}),
};

/**
 * Get icon size classes for menu items based on size variant
 * @param {('xs'|'sm'|'md'|'lg')} size
 * @returns {string} Tailwind size classes for the icon
 */
export const getMenuItemIconSizeClass = size => {
	if (size === 'xs' || size === 'sm') return 'h-3 w-3';
	if (size === 'lg') return 'h-5 w-5';
	return 'h-3.5 w-3.5';
};
